import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PlayIcon } from 'lucide-react';
import { pointsFor } from '../../hooks/useTodayPlan';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { equalShares, isDoneStatus } from '../../utils/progress';
import { namesOf } from '../../utils/members';
import { TodayItem } from '../../utils/today';
import { StatusBadge } from '../StatusBadge';
import { SyncTag } from '../SyncTag';
import { TaskCheckbox } from './TaskCheckbox';
interface TaskRowProps {
  item: TodayItem;
}
export function TaskRow({
  item
}: TaskRowProps) {
  const {
    template,
    occ,
    relation,
    counterpartId,
    excused,
    voluntary
  } = item;
  const {
    minutesToday,
    timer,
    viewer,
    family,
    memberById
  } = useAppState();
  const {
    openTask,
    requestTimerStart
  } = useUi();
  const done = isDoneStatus(occ.status);
  const minutes = template.targetMinutes ? minutesToday(template.id) : 0;
  const isTimerTarget = timer?.target.templateId === template.id && timer.status !== 'review';
  const others = template.assigneeIds.filter(id => id !== viewer.id);
  const given = relation === 'predano';
  const points = pointsFor(template, viewer.id);
  const reduce = useReducedMotion();

  // Ob kljukici kratko »+5« poleti navzgor – potrditev, da se je teden premaknil.
  const prevDone = useRef(done);
  const [burst, setBurst] = useState(0);
  useEffect(() => {
    if (done && !prevDone.current && points > 0 && !reduce) setBurst(b => b + 1);
    prevDone.current = done;
  }, [done, points, reduce]);
  const meta: string[] = [];
  if (template.group === 'hisa' && template.baseValue && template.valueType === 'osnovno' && template.assigneeIds.length > 1) {
    meta.push(`z: ${namesOf(family, others)} · tvoj delež ${equalShares(template.baseValue, template.assigneeIds)[viewer.id] ?? 0} od ${template.baseValue}`);
  }
  if (template.valueType === 'bonus' && template.baseValue) meta.push(`+${template.baseValue} goodies`);
  if (template.dueLabel && template.valueType !== 'bonus') meta.push(template.dueLabel);
  if (template.confirmation === 'skrbnik' && occ.status === 'odprto') meta.push('potrdi skrbnik');
  if (voluntary) meta.push('danes pouk – vadba je prostovoljna (goodies)');
  const relationTag = relation === 'ponujeno' ? {
    cls: 'bg-info-soft text-info-ink',
    text: `Ponujeno ${counterpartId === 'vsi' ? 'vsem' : memberById(counterpartId)?.name ?? ''} – še vedno tvoje`
  } : relation === 'prevzeto' ? {
    cls: 'bg-info-soft text-info-ink',
    text: `Prevzeto od: ${memberById(counterpartId)?.name ?? ''}`
  } : given ? {
    cls: 'bg-excused-soft text-excused-ink',
    text: `Opravi ${memberById(counterpartId)?.name ?? 'drug član'} – ta termin`
  } : null;
  return <li className={`flex items-center gap-2 py-1.5 pl-2 pr-3 ${excused || given ? 'opacity-70' : ''}`} data-id="element-1055" data-mp="2lyieawpx73xk">
      <span className="relative shrink-0" data-id="element-1056" data-mp="2hxf2qmewnu25">
        {given ? <span className="block h-11 w-11" aria-hidden="true" data-id="element-1057" data-mp="2pdsbuko3crzl" /> : <TaskCheckbox template={template} status={occ.status} excused={excused} data-id="element-1058" data-mp="2udeoarw3i47c" />}
        <AnimatePresence data-id="element-1059" data-mp="2ubbppjgvn2r0">
          {burst > 0 && <motion.span key={burst} initial={{
          opacity: 0,
          x: '-50%',
          y: 0,
          scale: 0.96
        }} animate={{
          opacity: [0, 1, 0],
          x: '-50%',
          y: -26,
          scale: 1
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} onAnimationComplete={() => setBurst(0)} className="pointer-events-none absolute left-1/2 top-0 font-display text-base text-done-ink" aria-hidden="true" data-id="element-1060" data-mp="2fhpahh12cxdp">
              +{points}
            </motion.span>}
        </AnimatePresence>
      </span>
      <button type="button" onClick={() => openTask(template.id)} className="min-w-0 flex-1 rounded-lg py-1.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-1061" data-mp="3r3f80chlxuhs">
        <span className={`block font-bold leading-snug ${done || excused || given ? 'text-muted' : 'text-ink'} ${excused ? 'line-through' : ''}`} data-id="element-1062" data-mp="2873rbmplvyrk">
          {template.title}
        </span>
        {template.targetMinutes !== undefined && !excused && <span className="mt-1 flex items-center gap-2" data-id="element-1063" data-mp="1t934rjamaza3">
            <span className="h-2 w-24 overflow-hidden rounded-full bg-line" aria-hidden="true" data-id="element-1064" data-mp="1tamltc43lecx">
              <span className={`block h-full rounded-full ${minutes >= template.targetMinutes ? 'bg-done' : 'bg-accent'}`} style={{
            width: `${Math.min(100, minutes / template.targetMinutes * 100)}%`
          }} data-id="element-1065" data-mp="2qwahfusqbsne" />
            </span>
            <span className="text-xs font-semibold tabular-nums text-muted" data-id="element-1066" data-mp="2c8pafpsqh9a7">
              {minutes}/{template.targetMinutes} min
            </span>
          </span>}
        {excused && <span className="mt-0.5 block text-xs font-semibold text-excused-ink" data-id="element-1067" data-mp="34w31565u98lv">Oproščeno – ne šteje kot zamujeno</span>}
        {meta.length > 0 && !excused && <span className="mt-0.5 block text-xs text-muted" data-id="element-1068" data-mp="3d72072zlanmu">{meta.join(' · ')}</span>}
        {(relationTag || occ.status !== 'odprto' || occ.sync === 'pending') && !excused && <span className="mt-1 flex flex-wrap items-center gap-1.5" data-id="element-1069" data-mp="3m55p899sa2je">
            {relationTag && <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${relationTag.cls}`} data-id="element-1070" data-mp="3r61o28fa2yhy">{relationTag.text}</span>}
            <StatusBadge status={occ.status} data-id="element-1071" data-mp="3w3ld83dikvoo" />
            {(occ.status !== 'odprto' || occ.sync === 'pending') && <SyncTag sync={occ.sync} data-id="element-1072" data-mp="3yhjch6wtbco" />}
          </span>}
        {occ.status === 'potrjeno' && occ.shares?.[viewer.id] !== undefined && template.assigneeIds.length > 1 && <span className="mt-0.5 block text-xs font-semibold text-done-ink" data-id="element-1073" data-mp="dosou868rslo">Moj delež: {occ.shares[viewer.id]} cvenkotočk</span>}
      </button>
      {points > 0 && !excused && !given && <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-extrabold tabular-nums ${done ? 'bg-done-soft text-done-ink' : 'bg-canvas text-muted'}`} aria-label={`${points} cvenkotočk`} data-id="element-1074" data-mp="np33lx7xd82y">
          +{points}
        </span>}
      {isTimerTarget ? <span className="shrink-0 rounded-full bg-accent-soft px-2.5 py-1 text-xs font-bold text-accent-ink" data-id="element-1075" data-mp="7qqyhxe700ru">
          {timer?.status === 'paused' ? 'Premor' : 'Teče'}
        </span> : !done && !excused && !given && occ.status !== 'v_pregledu' && template.timed && <button type="button" onClick={() => requestTimerStart({
      templateId: template.id
    })} aria-label={`Začni časovnik: ${template.title}`} className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent-soft text-accent-ink transition-colors duration-150 hover:bg-accent hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent" data-id="element-1076" data-mp="braugxn14yce">
            <PlayIcon className="h-4 w-4" fill="currentColor" aria-hidden="true" data-id="element-1077" data-mp="12zb20p9jmvm2" />
          </button>}
    </li>;
}